import React, { useCallback, useEffect, useMemo } from 'react'
import useStripeProductDetails from 'common/providers/useStripeProductDetails'
import useLegalDocumentRatings from 'common/providers/useLegalDocumentRatings'
import useLegalDocumentCode from 'common/providers/useLegalDocumentCode'
import { useNavigate, useParams } from 'react-router-dom'
import useProfile from 'common/providers/useProfile'
import useLegalDocument from 'common/providers/useLegalDocument'
import { useLegalDocumentMetadata } from 'common/hooks/useLegalDocumentMetadata'
import { useLegalDocumentCreation } from 'hooks/useLegalDocumentCreation'
import { useCollapsedHtmlContent } from 'hooks/useCollapsedHtmlContent'
import { setAuthRedirectCookie } from 'common/utils/redirects'
import { FAQ } from './components/FAQ'
import { TemplateCardBig } from './components/TemplateCardBig'
import { PurchaseAndCreateCard } from './components/PurchaseAndCreateCard'
import { DocumentHighlights } from './components/DocumentHighlights'
import { BackgroundDesktop, BackgroundMobile } from './components/Background'
import { Feedback } from './components/Feedback'

const LegalDocumentTemplate = () => {
  const { id } = useParams()
  const { profile } = useProfile()
  const navigate = useNavigate()
  const { legalDocument, getLegalDocument } = useLegalDocument()
  const metadata = useLegalDocumentMetadata(legalDocument)
  const { legalDocumentCode, getLegalDocumentCode } = useLegalDocumentCode()
  const { isCheckoutSessionLoading, createLegalDocument } =
    useLegalDocumentCreation()

  const {
    legalDocumentRatings,
    getLegalDocumentRatings,
    createLegalDocumentRating,
    updateLegalDocumentRating,
  } = useLegalDocumentRatings()

  const legalDocumentRating = legalDocumentRatings?.[0]
  const { rating } = legalDocumentRating ?? {}

  const { stripeProductDetails, getStripeProductDetails } =
    useStripeProductDetails()

  const stripeProductId = legalDocument?.type?.stripe_product_id

  useEffect(() => {
    getLegalDocument({ id })
    getLegalDocumentCode({ id })

    if (profile) {
      getLegalDocumentRatings({ legaldocument_id: id, user_id: profile?.id })
    }
  }, [id, profile?.id])

  useEffect(() => {
    if (stripeProductId) {
      getStripeProductDetails({ id: stripeProductId })
    }
  }, [stripeProductId])

  const { sections: faqSections } = useCollapsedHtmlContent(legalDocument?.faq)

  const loginWithRedirect = useCallback((action: 'download' | 'create') => {
    // Navigate to login, setting a cookie including a query param to indicate
    // that the user wants to download or create a legal document when
    // redirected back to the current page after login.
    setAuthRedirectCookie(
      `${window.location.href.split('?')[0]}?${action}=true`,
    )
    navigate('/login')
  }, [])

  const create = useCallback(() => {
    if (profile?.id) {
      createLegalDocument(stripeProductId)

      return
    }

    loginWithRedirect('create')
  }, [createLegalDocument, stripeProductId])

  const onDownloadClick = useCallback(() => {
    // Redirect to the download page if there is no stripe product ID associated
    // with this document, or if the user already has a valid download code.
    if (!stripeProductId || legalDocumentCode?.code) {
      window.location.href = `/legal-documents/${legalDocument.id}/download`

      return
    }

    create()
  }, [legalDocument, legalDocumentCode, stripeProductId, create])

  // Handle the callback to create or download a document after having been sent
  // through the login flow.
  useEffect(() => {
    const currentUrl = new URL(window.location.href)

    if (!!currentUrl.searchParams.get('create') && stripeProductId) {
      create()
    }
  }, [stripeProductId])

  const onRatingClick = useCallback(
    (rating: number, description: string) => {
      if (!profile) {
        navigate('/login')

        return
      }

      if (legalDocumentRating) {
        updateLegalDocumentRating(
          {
            rating_id: legalDocumentRating.id,
            legaldocument: legalDocument.id,
            rating,
          },
          {
            onSuccess: () => {
              getLegalDocumentRatings({
                legaldocument_id: legalDocument.id,
                user_id: profile?.id,
              })
            },
          },
        )

        return
      }

      createLegalDocumentRating(
        {
          legaldocument: legalDocument.id,
          rating,
        },
        {
          onSuccess: () => {
            getLegalDocumentRatings({
              legaldocument_id: legalDocument.id,
              user_id: profile?.id,
            })
          },
        },
      )
    },
    [profile?.id, legalDocument?.id, rating],
  )

  const price = useMemo(() => {
    if (!stripeProductDetails) {
      return null
    }

    const rawPrice = (
      stripeProductDetails.price.unit_amount / 100
    ).toLocaleString('en-GB', {
      style: 'currency',
      currency: stripeProductDetails.price.currency.toUpperCase(),
    })

    if (stripeProductDetails.price.tax_behavior === 'exclusive') {
      return `${rawPrice}`
    }

    return rawPrice
  }, [stripeProductDetails])

  if (!legalDocument) {
    return null
  }

  return (
    <div className='relative'>
      <div className='relative hidden lg:block -z-10'>
        <BackgroundDesktop />
      </div>

      <div className='flex flex-col gap-[80px] lg:pt-[60px]'>
        {/* Mobile */}
        <div className='relative flex flex-col items-center lg:hidden gap-6'>
          <div className='absolute top-[17px]'>
            <DocumentHighlights metadata={metadata} document={legalDocument} />
          </div>

          <div className='pr-[35px] pb-[74px] pt-[100px]'>
            <TemplateCardBig
              className=''
              template={legalDocument}
              onDownloadClick={onDownloadClick}
              isCheckoutSessionLoading={isCheckoutSessionLoading}
            />
          </div>
          <div className='relative flex flex-col gap-[100px]'>
            <div className='block lg:hidden -z-10'>
              <BackgroundMobile />
            </div>

            <PurchaseAndCreateCard
              legalDocumentCode={legalDocumentCode}
              price={price}
              className=''
            />

            <Feedback legalDocumentId={legalDocument?.id} />
          </div>
        </div>

        {/* Desktop */}
        <div className='hidden lg:flex'>
          <TemplateCardBig
            className=''
            template={legalDocument}
            onDownloadClick={onDownloadClick}
            isCheckoutSessionLoading={isCheckoutSessionLoading}
          />

          <div className='flex flex-grow max-w-[248px] min-w-[40px]' />

          <div className='flex flex-col items-center gap-[100px] pt-[80px] pr-[20px]'>
            <PurchaseAndCreateCard
              legalDocumentCode={legalDocumentCode}
              price={price}
              className=''
            />
            <DocumentHighlights metadata={metadata} document={legalDocument} />
            <Feedback legalDocumentId={legalDocument?.id} />
          </div>
        </div>

        <div className='pb-[84px] md:pb-[60px]'>
          <FAQ faqSections={faqSections} />
        </div>
      </div>
    </div>
  )
}

export default LegalDocumentTemplate
